import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Per-team financial dashboard.
 * Fetches /api/teams/{id}/financials and shows the current budget, the
 * upcoming obligations for the season, and a stadium snapshot.
 */
// Mappa il livello (chiave del riquadro finanziario) alla chiave upgrade dell'endpoint
const TIER_UPGRADE = {
    level_1: "foundation",
    level_2a: "stadium_2",
    level_2b: "ugly_2",
    level_3a: "stadium_3",
    level_3b: "ugly_3",
};
const TIER_PREREQ = {
    level_1: null,
    level_2a: "level_1",
    level_2b: "level_1",
    level_3a: "level_2a",
    level_3b: "level_2b",
};

export default function FinancialsPanel({ teamId, accent = "rgb(var(--tc-2bb6ff))", reloadKey = 0, canBuyStadium = false, summerOpen = false, controlledAdmin = false, onStadiumUpgraded }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [open, setOpen] = useState(true);
    const [busyTier, setBusyTier] = useState(null);
    const [stadiumErr, setStadiumErr] = useState("");

    const loadFinancials = () => {
        if (!teamId) return;
        api.get(`/teams/${teamId}/financials`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message));
    };

    useEffect(() => {
        loadFinancials();
    }, [teamId, reloadKey]);

    const buyUpgrade = async (tierKey, buildCost, label) => {
        const upg = TIER_UPGRADE[tierKey];
        if (!upg) return;
        if (!window.confirm(`Acquistare "${label}"?\n\nCosto: ${(buildCost / 1e6).toFixed(0)} mln EUR\nVerranno scalati dal budget squadra e versati al Tesoro OP.`)) return;
        setBusyTier(tierKey);
        setStadiumErr("");
        try {
            await api.post(`/teams/${teamId}/stadium/upgrade`, { upgrade: upg });
            loadFinancials();
            onStadiumUpgraded && onStadiumUpgraded();
        } catch (e) {
            setStadiumErr(e.response?.data?.detail || e.message);
        } finally {
            setBusyTier(null);
        }
    };

    if (err) return <Notice text={`Errore caricamento finanze: ${err}`} accent="rgb(var(--tc-ff3b30))" />;
    if (!data) return <Notice text="// CARICAMENTO BILANCIO SQUADRA…" accent="rgb(var(--tc-8f95a3))" />;

    const { team, payroll, next_obligations: obl, expected_income, projected_balance_end_season: projected, projected_balance_worst_case: projectedWorst, is_underwater, stadium, loan_buybacks: lb, pending_market: pend, club_dev_used_this_season } = data;
    const clubDevLocked = !!club_dev_used_this_season;
    const safeColor = is_underwater ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-2bb6ff))";
    const vg11Delta = payroll.vg11_delta_annual || 0;
    const divSavings = payroll.division_savings_annual || 0;
    const hasOptionScenario = lb && ((lb.option_out || 0) > 0 || (lb.option_in || 0) > 0);
    const hasPending = pend && (pend.count || 0) > 0;

    return (
        <section className="op-card overflow-hidden mb-8" data-testid="team-financials">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-t22252a hover:bg-t1a1d24/40 transition-colors"
                data-testid="financials-toggle"
            >
                <div className="flex items-center gap-3">
                    <span className="op-label" style={{ color: accent }}>// SCHEDA FINANZIARIA</span>
                    <span className="op-mono text-[10px] text-t8f95a3 uppercase">stagione corrente</span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="op-mono text-[10px] text-t8f95a3">Saldo previsto fine stagione:</span>
                    <span className="op-heading text-lg" style={{ color: safeColor }} data-testid="projected-balance">
                        {formatMln(projected)}
                    </span>
                    <span className="op-mono text-[10px] text-t8f95a3">{open ? "▲" : "▼"}</span>
                </div>
            </button>

            {open && (
                <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Budget + payroll breakdown */}
                    <div className="space-y-3">
                        <div className="op-mono text-[11px] text-t8f95a3 uppercase">Bilancio</div>
                        <Row label="Budget attuale" value={formatMln(team.budget)} bold accent="rgb(var(--tc-ffffff))" testid="row-budget" />
                        <div className="border-t border-t22252a pt-3">
                            <div className="op-mono text-[11px] text-t8f95a3 uppercase mb-2">Monte stipendi annuo</div>
                            <Row label="Stipendi base applicati" value={formatMln(payroll.base)} />
                            <Row
                                label={`Contributo VG11 (${payroll.vg11_rounded || "—"} → fattore ${payroll.vg11_factor.toFixed(2)})`}
                                value={`${vg11Delta >= 0 ? "+" : ""}${formatMln(vg11Delta)}`}
                                accent={vg11Delta >= 0 ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-2bb6ff))"}
                                testid="row-vg11-delta"
                            />
                            {divSavings > 0 && (
                                <Row
                                    label={`Sgravio ${payroll.division_code} (−${Math.round(payroll.division_discount * 100)}%)`}
                                    value={`−${formatMln(divSavings)}`}
                                    accent="rgb(var(--tc-2bb6ff))"
                                    testid="row-div-savings"
                                />
                            )}
                            <Row label="Totale annuo" value={formatMln(payroll.after_division)} bold accent="rgb(var(--tc-ffffff))" testid="row-annual-payroll" />
                        </div>
                        {expected_income && (
                            <div className="border-t border-t22252a pt-3">
                                <div className="op-mono text-[11px] text-t8f95a3 uppercase mb-2">Entrate fisse</div>
                                <Row
                                    label="Diritti TV BASE"
                                    value={`+${formatMln(expected_income.tv_rights_base)}`}
                                    accent="rgb(var(--tc-2bb6ff))"
                                    testid="row-tv-base"
                                />
                                <Row
                                    label={`Diritti TV ${payroll.division_code || "—"}`}
                                    value={`+${formatMln(expected_income.tv_rights_nl)}`}
                                    accent="rgb(var(--tc-2bb6ff))"
                                    testid="row-tv-rights"
                                />
                                {(expected_income.buyback_obligation || 0) > 0 && (
                                    <Row
                                        label="Incasso riscatti obbligatori"
                                        value={`+${formatMln(expected_income.buyback_obligation)}`}
                                        accent="rgb(var(--tc-7ce38b))"
                                        testid="row-buyback-in"
                                    />
                                )}
                                <Row
                                    label="Totale entrate fisse"
                                    value={`+${formatMln(expected_income.total)}`}
                                    bold accent="rgb(var(--tc-2bb6ff))"
                                    testid="row-income-fixed-total"
                                />
                                <div className="op-mono text-[9px] text-t5c6370 mt-1 italic">
                                    Premi piazzamento, coppe, Fair Play, capocannonieri si aggiungono a fine stagione.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Obligations */}
                    <div className="space-y-3">
                        <div className="op-mono text-[11px] text-t8f95a3 uppercase">Uscite previste stagione</div>
                        <Row label="Rata 1 stipendi base" value={formatMln(obl.stipend_base_rate_1)} accent="rgb(var(--tc-ff3b30))" testid="row-base-rate-1" paid={obl.base_rate_1_paid} />
                        <Row label="Rata 2 stipendi base" value={formatMln(obl.stipend_base_rate_2)} accent="rgb(var(--tc-ff3b30))" testid="row-base-rate-2" paid={obl.base_rate_2_paid} />
                        {obl.vg11_annual !== 0 && (
                            <>
                                <Row
                                    label={`Rata 1 VG11 (${payroll.vg11_rounded || "—"})`}
                                    value={formatMln(obl.vg11_rate_1)}
                                    accent={obl.vg11_annual > 0 ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-2bb6ff))"}
                                    testid="row-vg11-rate-1"
                                    paid={obl.vg11_rate_1_paid}
                                />
                                <Row
                                    label="Rata 2 VG11"
                                    value={formatMln(obl.vg11_rate_2)}
                                    accent={obl.vg11_annual > 0 ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-2bb6ff))"}
                                    testid="row-vg11-rate-2"
                                    paid={obl.vg11_rate_2_paid}
                                />
                            </>
                        )}
                        <Row
                            label="Tassa annuale 10% (su budget netto)"
                            value={formatMln(obl.annual_tax_10pct)}
                            accent="rgb(var(--tc-ff3b30))"
                            testid="row-tax"
                            paid={obl.annual_tax_paid}
                        />
                        {obl.annual_tax_base != null && (
                            <div className="op-mono text-[9px] text-t5c6370 -mt-2 italic" data-testid="row-tax-note">
                                10% di {formatMln(obl.annual_tax_base)} (budget − stipendi base − contributo VG11)
                            </div>
                        )}
                        {obl.stadium_maintenance > 0 && (
                            <Row label="Manutenzione stadio" value={formatMln(obl.stadium_maintenance)} accent="rgb(var(--tc-ff3b30))" testid="row-stadium" paid={obl.stadium_maintenance_paid} />
                        )}
                        {(obl.buyback_obligation || 0) > 0 && (
                            <Row label="Riscatti obbligatori (acquisti)" value={formatMln(obl.buyback_obligation)} accent="rgb(var(--tc-ff3b30))" testid="row-buyback-out" />
                        )}
                        <div className="border-t border-t22252a pt-3">
                            <Row label="Totale uscite" value={formatMln(obl.total)} bold accent="rgb(var(--tc-ff3b30))" testid="row-total" />
                            {expected_income?.total > 0 && (
                                <Row label="Totale entrate fisse" value={`+${formatMln(expected_income.total)}`} bold accent="rgb(var(--tc-2bb6ff))" testid="row-income-total" />
                            )}
                            <Row label="Saldo fine stagione" value={formatMln(projected)} bold accent={safeColor} testid="row-projected" />
                            {is_underwater && (
                                <div className="op-mono text-[10px] text-tff3b30 mt-2">⚠ Bilancio in rosso a fine stagione. Servono premi extra (coppe, capocannoniere) o riduzione monte stipendi.</div>
                            )}
                        </div>

                        {hasPending && (
                            <div className="border-t border-t22252a pt-3" data-testid="pending-market">
                                <div className="op-mono text-[11px] text-tff7a1a uppercase mb-2">Mercato in attesa · esecuzione differita</div>
                                {pend.cash_in > 0 && (
                                    <Row label="Incassi trasferimenti (in attesa)" value={`+${formatMln(pend.cash_in)}`} accent="rgb(var(--tc-7ce38b))" testid="row-pending-cash-in" />
                                )}
                                {pend.cash_out > 0 && (
                                    <Row label="Spese trasferimenti (in attesa)" value={`−${formatMln(pend.cash_out)}`} accent="rgb(var(--tc-ff3b30))" testid="row-pending-cash-out" />
                                )}
                                {pend.wage_added_annual > 0 && (
                                    <Row label="Stipendi acquisiti (annuo)" value={`−${formatMln(pend.wage_added_annual)}`} accent="rgb(var(--tc-ff3b30))" testid="row-pending-wage-in" />
                                )}
                                {pend.wage_removed_annual > 0 && (
                                    <Row label="Stipendi ceduti (annuo)" value={`+${formatMln(pend.wage_removed_annual)}`} accent="rgb(var(--tc-7ce38b))" testid="row-pending-wage-out" />
                                )}
                                <Row
                                    label="Impatto netto sul saldo"
                                    value={`${pend.net_balance_impact >= 0 ? "+" : "−"}${formatMln(Math.abs(pend.net_balance_impact))}`}
                                    bold accent={pend.net_balance_impact >= 0 ? "rgb(var(--tc-7ce38b))" : "rgb(var(--tc-ff3b30))"}
                                    testid="row-pending-net"
                                />
                                <div className="mt-2 space-y-1">
                                    {(pend.items || []).map((it) => {
                                        const isLoan = it.kind !== "transfer";
                                        const badgeLabel = it.kind === "loan_obligation"
                                            ? "PRESTITO+OBBLIGO"
                                            : it.kind === "loan_option"
                                                ? "PRESTITO+DIRITTO"
                                                : it.kind === "loan"
                                                    ? "PRESTITO"
                                                    : "DEFINITIVO";
                                        const badgeColor = it.is_counterparty ? "rgb(var(--tc-9d7cff))" : (isLoan ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-2bb6ff))");
                                        const badgeTitle = it.is_counterparty
                                            ? "Contropartita (giocatore scambiato)"
                                            : it.kind === "loan_obligation"
                                                ? "Prestito con obbligo di riscatto"
                                                : it.kind === "loan_option"
                                                    ? "Prestito con diritto di riscatto"
                                                    : it.kind === "loan"
                                                        ? "Prestito secco"
                                                        : "Trasferimento a titolo definitivo";
                                        const cashTxt = it.is_counterparty
                                            ? "contropartita"
                                            : (it.cash > 0
                                                ? `${it.cash_direction === "in" ? "+" : "−"}${formatMln(it.cash)}`
                                                : (it.has_counterparty ? "scambio" : "a zero"));
                                        return (
                                            <div key={`${it.deal_id}-${it.player}-${it.is_counterparty ? "cp" : "main"}`} className={`op-mono text-[10px] text-t8f95a3 flex justify-between items-center gap-2 ${it.is_counterparty ? "pl-3" : ""}`} data-testid={`pending-item-${it.deal_id}`}>
                                                <span className="truncate flex items-center gap-1.5 min-w-0">
                                                    <span
                                                        className="shrink-0 op-mono text-[8px] uppercase px-1 py-0.5 border leading-none"
                                                        style={{ borderColor: withAlpha(badgeColor, "66"), color: badgeColor }}
                                                        title={badgeTitle}
                                                        data-testid={`pending-kind-${it.deal_id}`}
                                                    >
                                                        {it.is_counterparty ? "CONTROPARTITA" : badgeLabel}
                                                    </span>
                                                    <span className="truncate">
                                                        {it.role === "buyer" ? "← " : "→ "}{it.player || "—"} <span className="text-t5c6370">({it.role === "buyer" ? "da" : "a"} {it.counterpart})</span>
                                                    </span>
                                                </span>
                                                <span className={`shrink-0 ${it.is_counterparty ? "text-t9d7cff" : (it.cash_direction === "in" ? "text-t7ce38b" : "text-tff7a1a")}`}>
                                                    {cashTxt}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="op-mono text-[9px] text-t5c6370 mt-1 italic">
                                    Affari conclusi a mercato chiuso, in attesa di esecuzione. Già inclusi nel «Saldo fine stagione».
                                </div>
                            </div>
                        )}

                        {hasOptionScenario && (
                            <div className="border-t border-t22252a pt-3" data-testid="loan-option-scenario">
                                <div className="op-mono text-[11px] text-te8ff00 uppercase mb-2">Riscatti opzionali (diritto · potenziale)</div>
                                {(lb.option_out || 0) > 0 && (
                                    <Row label="Diritti di riscatto da esercitare" value={`−${formatMln(lb.option_out)}`} accent="rgb(var(--tc-e8ff00))" testid="row-option-out" />
                                )}
                                {(lb.option_in || 0) > 0 && (
                                    <Row label="Diritti di riscatto altrui (su tuoi prestiti)" value={`+${formatMln(lb.option_in)}`} accent="rgb(var(--tc-e8ff00))" testid="row-option-in" />
                                )}
                                <Row label="Saldo se eserciti tutti i diritti" value={formatMln(projectedWorst)} bold accent={projectedWorst < 0 ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-e8ff00))"} testid="row-projected-worst" />
                                <div className="op-mono text-[9px] text-t5c6370 mt-1 italic">
                                    Il diritto di riscatto è una scelta: non è incluso nel saldo certo sopra.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Stadium */}
                    <div className="space-y-3">
                        <div className="op-mono text-[11px] text-t8f95a3 uppercase">Stadio (Art. 10)</div>
                        {canBuyStadium && (
                            <div className="op-mono text-[10px] leading-relaxed border border-t22252a bg-t0f1115 p-2.5" data-testid="stadium-buy-hint">
                                {controlledAdmin ? (
                                    <span className="text-tff3b30">🔒 Squadra in Amministrazione Controllata: acquisti stadio bloccati.</span>
                                ) : clubDevLocked ? (
                                    <span className="text-tff9d2b">🔒 Hai già fatto un intervento di sviluppo club (stadio o struttura) questa stagione. Prossima stagione potrai farne un altro.</span>
                                ) : !summerOpen ? (
                                    <span className="text-t8f95a3">🔒 Gli ampliamenti si acquistano solo a mercato di inizio stagione (finestra estiva aperta).</span>
                                ) : (
                                    <span className="text-te8ff00">✓ Mercato estivo aperto: puoi acquistare gli ampliamenti disponibili (budget → Tesoro OP).</span>
                                )}
                            </div>
                        )}
                        {stadiumErr && (
                            <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 op-mono text-[10px] p-2" data-testid="stadium-buy-err">
                                {stadiumErr}
                            </div>
                        )}
                        {stadium.map((s) => {
                            const ownedMap = Object.fromEntries(stadium.map((x) => [x.key, x.owned]));
                            const prereq = TIER_PREREQ[s.key];
                            const prereqOk = prereq == null || ownedMap[prereq];
                            const buyable = canBuyStadium && !s.owned && prereqOk && TIER_UPGRADE[s.key];
                            const canAfford = (team?.budget || 0) >= s.build_cost;
                            const disabled = !summerOpen || controlledAdmin || clubDevLocked || !canAfford || busyTier === s.key;
                            return (
                            <div
                                key={s.key}
                                className={`border p-2.5 transition-opacity ${s.owned ? "border-te8ff00/50 bg-te8ff00/[0.04]" : "border-t1a1d24 opacity-50"}`}
                                data-testid={`stadium-tier-${s.key}`}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <div className={`op-mono text-[12px] ${s.owned ? "text-tffffff" : "text-t5c6370"}`}>{s.label}</div>
                                    <div className={`op-mono text-[10px] px-2 py-0.5 border ${s.owned ? "text-te8ff00 border-te8ff00/50 bg-te8ff00/[0.06]" : "text-t5c6370 border-t22252a"}`}>
                                        {s.owned ? "✓ COSTRUITO" : "non posseduto"}
                                    </div>
                                </div>
                                <div className={`op-mono text-[10px] ${s.owned ? "text-tc9ccd3" : "text-t5c6370"}`}>{s.benefit}</div>
                                <div className="op-mono text-[10px] text-t5c6370 mt-1">
                                    Costr. <span className={s.owned ? "text-tffffff" : "text-t8f95a3"}>{formatMln(s.build_cost)}</span>
                                    {s.annual_cost > 0 && <> · Annuale <span className={s.owned ? "text-tffffff" : "text-t8f95a3"}>{formatMln(s.annual_cost)}</span></>}
                                </div>
                                {buyable && (
                                    <button
                                        onClick={() => buyUpgrade(s.key, s.build_cost, s.label)}
                                        disabled={disabled}
                                        className="mt-2 w-full op-mono text-[11px] uppercase tracking-wider py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        style={{ borderColor: "rgb(var(--tc-e8ff00))", color: "rgb(var(--tc-e8ff00))" }}
                                        title={controlledAdmin ? "Amministrazione Controllata: bloccato" : clubDevLocked ? "Già fatto un intervento di sviluppo club questa stagione" : !summerOpen ? "Solo a mercato estivo aperto" : !canAfford ? "Budget insufficiente" : `Acquista ${s.label}`}
                                        data-testid={`stadium-buy-${s.key}`}
                                    >
                                        {busyTier === s.key ? "..." : !canAfford ? `Budget insufficiente (${(s.build_cost / 1e6).toFixed(0)}mln)` : `Acquista (${(s.build_cost / 1e6).toFixed(0)} mln)`}
                                    </button>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
}

function Row({ label, value, bold, accent, testid, paid }) {
    return (
        <div className="flex justify-between items-center" data-testid={testid}>
            <span className={`op-mono text-[11px] flex items-center gap-1.5 ${bold ? "text-tffffff" : paid ? "text-t5c6370" : "text-tc9ccd3"}`}>
                {label}
                {paid && (
                    <span className="op-mono text-[8px] uppercase px-1 py-0.5 border border-t5c6370/60 text-t5c6370 leading-none whitespace-nowrap" data-testid={testid ? `${testid}-paid` : undefined}>
                        già prelevata
                    </span>
                )}
            </span>
            <span
                className={`op-mono ${bold ? "text-[14px] font-bold" : "text-[12px]"} ${paid ? "line-through opacity-60" : ""}`}
                style={{ color: paid ? "rgb(var(--tc-5c6370))" : (accent || (bold ? "rgb(var(--tc-ffffff))" : "rgb(var(--tc-ffffff))")) }}
            >
                {value}
            </span>
        </div>
    );
}

function Notice({ text, accent = "rgb(var(--tc-8f95a3))" }) {
    return (
        <div className="op-card p-5 mb-8 op-mono text-[11px]" style={{ color: accent }}>{text}</div>
    );
}

function formatMln(v) {
    if (v == null) return "—";
    const n = v / 1_000_000;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)}mld`;
    if (Math.abs(n) >= 100) return `${n.toFixed(0)}mln`;
    return `${n.toFixed(2)}mln`;
}
